import { Fragment, useEffect, useState } from "react";
import {
  PaymentRequestButtonElement,
  useStripe,
} from "@stripe/react-stripe-js";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { showError } from "../actions/checkoutStore";
import { allowedCountries } from "../utils/StripePaymentRequestUtils";

export default function StripePaymentRequest() {
  const dispatch = useDispatch();
  const state = useSelector((state) => state);
  const stripe = useStripe();
  const { t } = useTranslation();
  const [paymentRequest, setPaymentRequest] = useState(null);
  const { information, summary, urls } = state.checkoutStore;

  useEffect(() => {
    if (stripe) {
      if (!allowedCountries.includes(information.order_country)) return;
      let amount = parseInt(summary.raw_total * summary.subunit_to_unit);

      let paymentRequestConfig = {
        country: information.order_country,
        currency: information.currency_code,
        total: {
          label: t("summary.total"),
          amount: amount,
        },
        requestPayerName: true,
        requestPayerEmail: true,
        requestPayerPhone: true,
      };

      if (!information.no_shipping_required)
        paymentRequestConfig.requestShipping = true;
      // Not all currencies are enabled for this functionality
      const pr = stripe.paymentRequest(paymentRequestConfig);

      // Check the availability of the Payment Request API.
      pr.canMakePayment().then((result) => {
        if (result) {
          pr.on("paymentmethod", handlePaymentMethodReceived);

          if (!information.no_shipping_required) {
            pr.on("shippingaddresschange", handleShippingAddress);
            pr.on("shippingoptionchange", handleShippingOption);
          }

          setPaymentRequest(pr);
        }
        return;
      });
    }
  }, [stripe]);

  const stripeStyleOptions = {
    paymentRequest,
    style: {
      paymentRequestButton: {
        type: "default",
        // One of 'default', 'book', 'buy', or 'donate'
        // Defaults to 'default'

        theme: "dark",
        // One of 'dark', 'light', or 'light-outline'
        // Defaults to 'dark'

        height: "30px",
        // Defaults to '40px'. The width is always '100%'.
      },
    },
  };

  const handleShippingOption = async (event) => {
    await axios({
      headers: {
        common: {
          "Content-Type": "application/json",
        },
      },
      method: "post",
      url: urls.change_shipping_option,
      data: {
        shipping_method_id: event.shippingOption.id,
      },
      params: { recover_token: window.token },
    }).then(function (result) {
      event.updateWith({
        status: "success",
        total: {
          label: t("summary.total"),
          amount: parseInt(result.data.amount * summary.subunit_to_unit),
        },
      });
    });
  };

  const handleShippingAddress = async (event) => {
    if (!allowedCountries.includes(event.shippingAddress.country)) {
      event.updateWith({ status: "invalid_shipping_address" });
    } else {
      // Perform server-side request to fetch shipping options
      const shippingAddress = {
        shipping: {
          name: event.shippingAddress.recipient,
          phone: event.shippingAddress.phone,
          address: {
            line1: event.shippingAddress.addressLine[0],
            city: event.shippingAddress.city,
            postal_code: event.shippingAddress.postalCode,
            state: event.shippingAddress.region,
            country: event.shippingAddress.country,
          },
        },
      };

      const response = await axios({
        headers: {
          common: {
            "Content-Type": "application/json",
          },
        },
        method: "post",
        url: urls.estimate_shipping,
        data: shippingAddress,
        params: { recover_token: window.token },
      }).then(function (result) {
        if (result.data.length > 0) {
          const shippingOptions = result.data.map((estimate) => {
            const shipping_method_id = estimate.table.shipping_method_id;
            const shipping_service_id = estimate.table.shipping_service_id
              ? estimate.table.shipping_service_id
              : "";
            let amount = parseInt(
              estimate.table.price * summary.subunit_to_unit,
            );

            return {
              id: `${shipping_method_id}-${shipping_service_id}`,
              label: estimate.table.shipping_method_name,
              detail: estimate.table.description,
              amount: amount,
            };
          });
          event.updateWith({
            status: "success",
            shippingOptions: shippingOptions,
          });
        } else {
          event.updateWith({
            status: "fail",
          });
        }
      });
    }
  };

  const handlePaymentMethodReceived = async (event) => {
    const paymentDetails = {
      payment_method: event.paymentMethod.id,
      billing_details: {
        name: event.paymentMethod.billing_details.name,
        email: event.paymentMethod.billing_details.email,
        address: event.paymentMethod.billing_details.address,
        phone: event.paymentMethod.billing_details.phone,
      },
      shipping_details: event.shippingAddress,
      shipping_option: event.shippingOption,
      recover_token: window.token,
    };

    const response = await fetch(urls.create_payment_intent, {
      method: "post",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ paymentDetails }),
    }).then((res) => {
      return res.json();
    });

    if (response.status == "requires_confirmation") {
      const { error, paymentIntent } = await stripe.confirmCardPayment(
        response.client_secret,
        { payment_method: event.paymentMethod.id, amount: event.amount },
        { handleActions: false },
      );

      if (error) {
        console.log(error);
        return;
      }

      if (paymentIntent.status === "succeeded") {
        const response = await axios({
          headers: {
            common: {
              "Content-Type": "application/json",
            },
          },
          method: "put",
          url: urls.fast_place_order,
          data: paymentIntent,
          params: { recover_token: window.token },
        })
          .then((_response) => {
            event.complete("success");
            window.location = urls.success;
          })
          .catch((error) => {
            event.complete("fail");
            dispatch(showError(error));
          });
      } else {
        console.warn(
          `Unexpected status: ${paymentIntent.status} for ${paymentIntent}`,
        );
      }
    } else if (response.status === "succeeded") {
      event.complete("success");
      window.location = urls.success;
    } else {
      event.complete("fail");
    }
  };

  return (
    <Fragment>
      <div className="col-12">
        {paymentRequest ? (
          <fieldset className="mb-4">
            <legend>{t("information.express-checkout")}</legend>
            <div className="row">
              <div className="col-6 offset-3">
                <PaymentRequestButtonElement options={stripeStyleOptions} />
              </div>
            </div>
          </fieldset>
        ) : (
          <span></span>
        )}
      </div>
    </Fragment>
  );
}
